import React from 'react';
import { Plus, EyeOff, Eye, Trash2, Gift } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { formatDateTime } from '../../lib/utils';
import { Pagination } from '../../components/Pagination';

export const OffersTable = ({
    paginatedOffers,
    currentPage,
    totalPages,
    totalItems,
    setCurrentPage,
    onCreateNew,
    onToggleActive,
    onDelete,
    selectedIds = [],
    onToggleSelect = () => {},
    onToggleSelectAll = () => {},
    canBulkDelete = false,
}) => {
    const allSelected = canBulkDelete && paginatedOffers.length > 0
        && paginatedOffers.every(o => selectedIds.includes(o.id));
    return (
        <Card>
            <CardHeader>
                <CardTitle>Liste des offres</CardTitle>
            </CardHeader>
            <CardContent>
                {totalItems === 0 ? (
                    <div className="text-center py-12">
                        <Gift className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                        <p className="text-muted-foreground">Aucune offre creee</p>
                        <Button onClick={onCreateNew} className="mt-4 bg-jungle hover:bg-jungle/90">
                            <Plus className="h-4 w-4 mr-2" />
                            Creer votre premiere offre
                        </Button>
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                {canBulkDelete && (
                                    <TableHead className="w-10">
                                        <Checkbox
                                            checked={allSelected}
                                            onCheckedChange={() => onToggleSelectAll(paginatedOffers.map(o => o.id))}
                                            data-testid="offers-select-all"
                                        />
                                    </TableHead>
                                )}
                                <TableHead>Titre</TableHead>
                                <TableHead>Produits</TableHead>
                                <TableHead>Clients cibles</TableHead>
                                <TableHead>Date creation</TableHead>
                                <TableHead>Statut</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {paginatedOffers.map((offer) => (
                                <TableRow key={offer.id} data-testid={`offer-row-${offer.id}`}>
                                    {canBulkDelete && (
                                        <TableCell>
                                            <Checkbox
                                                checked={selectedIds.includes(offer.id)}
                                                onCheckedChange={() => onToggleSelect(offer.id)}
                                                data-testid={`offer-select-${offer.id}`}
                                            />
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        <div className="font-medium">{offer.title}</div>
                                        {offer.description && (
                                            <div className="text-sm text-muted-foreground truncate max-w-xs">
                                                {offer.description}
                                            </div>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Badge className="bg-blue-100 text-blue-800">
                                            {offer.products?.length || 0} produit(s)
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <Badge className="bg-purple-100 text-purple-800">
                                            {offer.target_client_ids?.length || 0} client(s)
                                        </Badge>
                                    </TableCell>
                                    <TableCell>{formatDateTime(offer.created_at)}</TableCell>
                                    <TableCell>
                                        <Badge className={offer.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                                            {offer.is_active ? 'Active' : 'Inactive'}
                                        </Badge>
                                        {(offer.validations?.length > 0) && (
                                            <Badge className="bg-emerald-100 text-emerald-800 ml-1" data-testid={`offer-validated-badge-${offer.id}`}>
                                                {offer.validations.length} commande(s)
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex justify-end gap-1">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => onToggleActive(offer)}
                                                className={offer.is_active ? 'hover:bg-red-100' : 'hover:bg-green-100'}
                                                title={offer.is_active ? 'Desactiver' : 'Activer'}
                                            >
                                                {offer.is_active ? (
                                                    <EyeOff className="h-4 w-4 text-red-500" />
                                                ) : (
                                                    <Eye className="h-4 w-4 text-green-600" />
                                                )}
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                                onClick={() => onDelete(offer)}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
            <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} setCurrentPage={setCurrentPage} label="offres" />
        </Card>
    );
};
